'use client';

import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { client } from '@/lib/api';
import { authClient } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { identifyUser, resetUser } from '@/utils/posthog';

/**
 * PostHog User Identification Component
 *
 * Automatically identifies users with PostHog when authenticated.
 * - Watches better-auth session for changes
 * - Fetches additional user data (avatar) from API
 * - Identifies user with PostHog including email, name, and avatar
 * - Resets PostHog identity on logout
 *
 * This component renders nothing - it only handles side effects.
 */
export function PostHogIdentify() {
  const { data: session } = authClient.useSession();
  const [lastIdentifiedUserId, setLastIdentifiedUserId] = useState<
    string | null
  >(null);

  // Fetch user profile data (includes additional info beyond session)
  const { data: userProfile } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const response = await client.user.getProfile();
      return response;
    },
    enabled: !!session?.user?.id, // Only fetch when user is authenticated
  });

  // Identify user when session changes
  useEffect(() => {
    if (session?.user && userProfile && !lastIdentifiedUserId) {
      const { id, email, name } = session.user;

      // Get avatar from profile
      const avatar = userProfile.image || '';

      try {
        identifyUser(id, {
          email: email || '',
          avatar,
          name: name || email || 'Unknown',
          emailVerified: userProfile.emailVerified || false,
        });

        setLastIdentifiedUserId(id);
      } catch (error) {
        logger.captureException(error as Error, {
          context: { userId: id, email },
          tags: {
            feature: 'analytics',
            provider: 'posthog',
            action: 'identify',
          },
        });
      }
    } else if (!session?.user && lastIdentifiedUserId) {
      // User logged out - reset PostHog
      try {
        resetUser();
        setLastIdentifiedUserId(null);

        logger.info('User identity reset in PostHog');
      } catch (error) {
        logger.captureException(error as Error, {
          tags: { feature: 'analytics', provider: 'posthog', action: 'reset' },
        });
      }
    }
  }, [session?.user, userProfile, lastIdentifiedUserId]);

  // This component doesn't render anything
  return null;
}

export default PostHogIdentify;
